import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { affiliateAPI } from '../api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, DollarSign, TrendingUp, Clock, CheckCircle, XCircle,
  Copy, ExternalLink, Gift, ArrowUpRight, Loader2, RefreshCw,
  Calendar, BarChart3, Link2
} from 'lucide-react';

/**
 * Affiliate Portal Dashboard
 * Shows affiliate's referral stats, earnings, and marketing resources
 */
export const AffiliatePortalPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [resources, setResources] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, resRes] = await Promise.all([
        affiliateAPI.getDashboard(),
        affiliateAPI.getResources()
      ]);
      setDashboard(dashRes.data);
      setResources(resRes.data);
    } catch (error) {
      toast.error('Failed to load affiliate data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      contacted: 'bg-blue-100 text-blue-700',
      purchased: 'bg-emerald-100 text-emerald-700',
      expired: 'bg-slate-100 text-slate-500',
      declined: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { affiliate, stats, recent_referrals, monthly_timeline } = dashboard || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Affiliate Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {affiliate?.contact_name || 'Affiliate'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-lg px-3 py-1">
            <Gift className="h-4 w-4 mr-2" />
            Code: {affiliate?.referral_code}
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_referrals || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.pending || 0} pending • {stats?.contacted || 0} contacted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Successful Conversions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{stats?.purchased || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.conversion_rate?.toFixed(1)}% conversion rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              Total Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              ${(stats?.total_earnings || 0).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              ${(stats?.pending_earnings || 0).toFixed(2)} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              Commission Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {((affiliate?.commission_rate || 0.10) * 100).toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {affiliate?.cashback_days || 30} day window
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="referrals">
            <Users className="h-4 w-4 mr-2" />
            Referrals
          </TabsTrigger>
          <TabsTrigger value="resources">
            <Link2 className="h-4 w-4 mr-2" />
            Resources
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Monthly Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Monthly Performance
                </CardTitle>
                <CardDescription>Your referral activity over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {monthly_timeline && Object.keys(monthly_timeline).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(monthly_timeline).map(([month, data]) => (
                        <div key={month} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{month}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.referrals} referrals • {data.conversions} conversions
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">${data.earnings.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No activity yet this period</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
                <CardDescription>At a glance performance metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg text-white">
                  <p className="text-sm opacity-80">Paid Out</p>
                  <p className="text-3xl font-bold">${(stats?.paid_earnings || 0).toFixed(2)}</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg text-white">
                  <p className="text-sm opacity-80">Pending Payment</p>
                  <p className="text-3xl font-bold">${(stats?.pending_earnings || 0).toFixed(2)}</p>
                </div>
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-sm text-muted-foreground">Expired Referrals</p>
                  <p className="text-2xl font-bold text-slate-600">{stats?.expired || 0}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <Card>
            <CardHeader>
              <CardTitle>Recent Referrals</CardTitle>
              <CardDescription>
                Track the status of your referred customers (privacy protected)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                {recent_referrals && recent_referrals.length > 0 ? (
                  <div className="space-y-3">
                    {recent_referrals.map((referral, idx) => (
                      <div
                        key={referral.id || idx}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Referral #{idx + 1}</p>
                            <p className="text-sm text-muted-foreground">
                              {referral.referred_at
                                ? format(new Date(referral.referred_at), 'MMM d, yyyy')
                                : 'Unknown date'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {referral.status === 'purchased' && referral.commission_amount && (
                            <div className="text-right">
                              <p className="font-bold text-emerald-600">
                                +${referral.commission_amount.toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {referral.commission_paid ? 'Paid' : 'Pending'}
                              </p>
                            </div>
                          )}
                          {getStatusBadge(referral.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No referrals yet</p>
                    <p className="text-sm">Share your referral code to get started!</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Referral Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Your Referral Links
                </CardTitle>
                <CardDescription>Share these links to track referrals</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {resources?.referral_links && Object.entries(resources.referral_links).map(([name, url]) => (
                  <div key={name} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium capitalize">{name} Link</p>
                      <p className="text-xs text-muted-foreground truncate">{url}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(url)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Commission Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Commission Details
                </CardTitle>
                <CardDescription>Your earning structure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg text-white">
                  <p className="text-sm opacity-80">Your Commission Rate</p>
                  <p className="text-4xl font-bold">{resources?.commission_info?.rate || '10%'}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Cashback Window</p>
                    <p className="font-bold">{resources?.commission_info?.cashback_window || '30 days'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Payment Schedule</p>
                    <p className="font-bold">{resources?.commission_info?.payment_schedule || 'Monthly'}</p>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> When a customer you refer makes a purchase within {resources?.commission_info?.cashback_window || '30 days'}, you earn {resources?.commission_info?.rate || '10%'} commission on their order.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AffiliatePortalPage;
